import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { MeResponseSchema } from '../../api';
import type { ApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useApiQuery } from '../../lib/api/use-api-query';
import { BackButton } from '../../lib/telegram/BackButton';
import { usePositions } from '../positions/use-positions';

import { EmergencyStopResult } from './EmergencyStopResult';
import { EmergencyStopSheet } from './EmergencyStopSheet';
import { channelsToPause, closeOptionFrom } from './emergency-model';
import { isWriting, useEmergencyStop } from './use-emergency-stop';

const HOME = '/';

/**
 * Screen E10 · Emergency stop (specs/M4-emergency-stop/spec.md; US-14; mockup 8). Opened in one
 * tap from Home (M1 R12); one more tap confirms (R1, provisional until Q50). After the stop, the
 * summary replaces the sheet until the trader goes back Home. "Retry closing" (Q104) opens the
 * sheet again over the summary, with `GET /me` and `GET /positions` read anew, so the trader sees
 * each position's current result before closing again (FXM-02 §6 case 6).
 *
 * E10 does not poll: closes still running are followed on E8, whose 5 s refresh is M7's (M4 R12).
 */
export function EmergencyStopScreen(): ReactElement {
  const navigate = useNavigate();
  const emergency = useEmergencyStop();
  const { stop, retryChannel, clearError, positionsRead } = emergency;
  // Bumped by "Retry closing": a new key remounts the sheet, so its reads start again.
  const [sheetRound, setSheetRound] = useState(0);
  const [retryingClose, setRetryingClose] = useState(false);
  const writing = isWriting(emergency);
  const sheetOpen = emergency.summary === null || retryingClose;

  const goHome = useCallback(() => {
    void navigate(HOME);
  }, [navigate]);

  // A sheet opened from the summary returns to it; the first one returns Home.
  const cancelSheet = (): void => {
    if (emergency.summary === null) goHome();
    else setRetryingClose(false);
  };

  return (
    <>
      {/* While a write is in flight the screen cannot be left, like Escape on the sheet (C3 R11). */}
      <BackButton
        onBack={() => {
          if (writing) return;
          if (sheetOpen) cancelSheet();
          else goHome();
        }}
      />
      {emergency.summary !== null && (
        <EmergencyStopResult
          summary={emergency.summary}
          channelErrors={emergency.channelErrors}
          writing={writing}
          onRetryChannel={(subscriptionId) => void retryChannel(subscriptionId)}
          onRetryClose={() => {
            clearError();
            setSheetRound((round) => round + 1);
            setRetryingClose(true);
          }}
        />
      )}
      {sheetOpen && (
        <ConfirmStop
          key={sheetRound}
          retryClose={emergency.summary !== null}
          stopping={emergency.stopping}
          error={emergency.error}
          onConfirm={(closePositions) => {
            void stop({ closePositions }).then((stopped) => {
              if (stopped) setRetryingClose(false);
            });
          }}
          onCancel={cancelSheet}
          onPositionsRead={positionsRead}
        />
      )}
    </>
  );
}

/** Props of {@link ConfirmStop}. */
interface ConfirmStopProps {
  /** Opened by "Retry closing" over the summary (Q104), not by the first stop. */
  readonly retryClose: boolean;
  /** A stop is in flight. */
  readonly stopping: boolean;
  /** Failure of the last stop, shown in the sheet. */
  readonly error: ApiError | null;
  /** Confirm tapped with the trader's choice. */
  readonly onConfirm: (closePositions: boolean) => void;
  /** Cancel tapped. */
  readonly onCancel: () => void;
  /** The ids of every open position, once `GET /positions` answers (used for "Retry closing"). */
  readonly onPositionsRead: (openIds: ReadonlySet<string>) => void;
}

/**
 * The sheet with its two reads: `GET /me` (how many channels) and `GET /positions` (the FXMate
 * positions and their results, M5's hook), each once, without polling. Neither is needed to stop:
 * a failure only changes what the sheet says (FXM-02 §7).
 */
function ConfirmStop({
  retryClose,
  stopping,
  error,
  onConfirm,
  onCancel,
  onPositionsRead,
}: ConfirmStopProps): ReactElement {
  const api = useApiClient();
  const loadMe = useCallback(
    (signal: AbortSignal) => api.get('/me', MeResponseSchema, signal),
    [api],
  );
  const me = useApiQuery(loadMe);
  // One read, never polled (M4 §5 "Read"): no `poll` option. Its failure is `positions.error`,
  // which disables the close option (M4 AC11).
  const positions = usePositions();
  // Q104: on "Retry closing", the fresh list tells which failed or running closes are no longer
  // open, so the summary stops offering to close them.
  useEffect(() => {
    if (!retryClose || positions.data === undefined) return;
    onPositionsRead(new Set(positions.data.positions.map((position) => position.id)));
  }, [retryClose, positions.data, onPositionsRead]);
  return (
    <EmergencyStopSheet
      retryClose={retryClose}
      channelCount={channelsToPause(me.data)}
      closeOption={closeOptionFrom(positions.data, positions.error !== undefined)}
      stopping={stopping}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
